import '../CommonCommericalSurvey/CustomExpression';
import '../CommonCommericalSurvey/CustomProperties';
import '../CommonCommericalSurvey/CustomWidgets';
import '../CommonCommericalSurvey/CommercialSurvey.css';

import { useApolloClient } from '@apollo/client';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import { Model, SurveyModel } from 'survey-core';
import { Survey } from 'survey-react-ui';

import Banner from '../../components/Banner/Banner';
import Loading from '../../components/Loading/Loading';
import Modal from '../../components/Modal/Modal';
import Navigation from '../../components/Navigation/Navigation';
import WalkExitModal from '../../components/WalkExitModal/WalkExitModal';
import {
  ApplicationResultKind,
  GetApplicationWalkDocument,
  GetApplicationWalkQuery,
  GetApplicationWalkQueryVariables,
  GetLobShellStateDocument,
  GetLobShellStateQuery,
  GetLobShellStateQueryVariables,
  useCompleteApplicationMutation
} from '../../generated/graphql';
import { toProduct, trackEvent, trackQuoteStep } from '../../utils/analytics';
import {
  clearApplicationEdit,
  EDIT_CONFLICT_MESSAGE,
  getApplicationEditId,
  isApplicationEditInProgress,
  RESUBMIT_TO_SAVE_MESSAGE,
  TabWalk
} from '../../utils/applicationEditSession';
import registerAttestationTextMarkdown from '../../utils/attestationTextMarkdown';
import { getSiblingMunichCoverageKeys } from '../../utils/lobCoverage';
import { setPhase2ResumePageName } from '../../utils/phase2ResumePage';
import { stripFirstPageIdentityFields } from '../../utils/prefillAnswers';
import { readTabWalk } from '../../utils/readTabWalk';
import setMaxDateForRetroactiveDate from '../../utils/setMaxDateForRetroactiveDate';
import {
  applyClearedTextAnswers,
  formatDateAnswers,
  isClearableTextQuestion,
  isEmptyTextAnswer
} from '../../utils/SurveyJSQuestion';
import { SurveyCSS } from '../CommonCommericalSurvey/commonVariables';
import fetchThirdJson from './fetchThirdJson';

interface LobShellState {
  phase: 3;
  fetchThirdJSONs: string[];
  carrierPartner: string;
  phase2LastPageName: string;
  /**
   * Filename of the Phase 2 (biz_coverage / pre-split combined) JSON the user
   * just left. Forwarded as `jsonName` to the fallback completeApplication
   * call when Phase 3 JSON fails to load. Optional for back-compat with
   * navigation state from older frontend builds.
   */
  phase2JsonFileName?: string;
  country: string;
  province: string;
  // One Application id per Phase 3 LOB in fetchThirdJSONs; index 0 is the
  // entry app (lobType depends on Phase 2 selection). Optional for back-compat.
  lobApplicationIds?: string[];
  currentLobIndex?: number;
}

type RecoveryStatus = 'idle' | 'loading' | 'failed';

// A LOB's saved answers must not carry the sibling LOB's coverages — they ride
// in from previousAnswers/phase2 (the parent Phase 2 selected both, and
// createLobApplication clones the full Phase 2 answer set into each sibling).
// Drop the whole sibling family — toggle AND add-on sub-coverages/limits, not
// just the toggle — so each per-LOB Application/Policy stores only its own
// coverage. Keeps endorsement inheritance single-LOB too.
const stripSiblingCoverage = (
  answers: Record<string, unknown>,
  lob: string
): void => {
  getSiblingMunichCoverageKeys(lob, Object.keys(answers)).forEach((key) => {
    delete answers[key];
  });
};

// Phase 3 (Munich GL) defines no Amtrust/WC questions — any Amtrust_* answer is
// inherited from the endorsed policy. Strip it so a GL endorsement's answers
// don't carry WC and surface an Amtrust coverage on the quote page.
const stripAmtrustCoverage = (answers: Record<string, unknown>): void => {
  Object.keys(answers).forEach((key) => {
    if (key.startsWith('Amtrust')) delete answers[key];
  });
};

const LobShell: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const { lobType, applicationId } = useParams<{
    lobType: string;
    applicationId: string;
  }>();
  const apolloClient = useApolloClient();

  // State source: router state only when present — just the internal LOB→LOB
  // hops pass it forward. Otherwise (first entry from Phase 2, and cold-loads:
  // refresh / pasted URL / email link) derive it from the backend via getLobShellState.
  const stateFromHistory = location.state as LobShellState | null;
  const [recoveredState, setRecoveredState] = useState<LobShellState | null>(
    null
  );
  const [recoveryStatus, setRecoveryStatus] = useState<RecoveryStatus>(
    stateFromHistory ? 'idle' : 'loading'
  );

  // Edit Identity: a cold load reads the walk the server has open and
  // reconciles this tab with it (reconcileTabWalk) before recovering its
  // state; it never opens one. A tab whose walk is gone — replaced in another
  // tab, or submitted — is led out instead (WalkExitModal). The internal
  // LOB→LOB hop skips this: its walk was current a moment ago, and a write it
  // makes after a replacement is refused and handled then (showWriteFailure).
  const [walkExit, setWalkExit] = useState<Exclude<
    TabWalk,
    { status: 'current' }
  > | null>(null);
  useEffect(() => {
    if (stateFromHistory) return;
    let cancelled = false;
    readTabWalk(apolloClient, applicationId)
      .then(async (walk) => {
        if (cancelled) return;
        if (walk.status !== 'current') {
          setWalkExit(walk);
          return;
        }
        const { data } = await apolloClient.query<
          GetLobShellStateQuery,
          GetLobShellStateQueryVariables
        >({
          query: GetLobShellStateDocument,
          variables: { applicationId },
          fetchPolicy: 'network-only'
        });
        if (cancelled) return;
        const recovered = data?.getLobShellState;
        if (recovered) {
          setRecoveredState({
            ...recovered,
            phase: 3,
            lobApplicationIds: recovered.lobApplicationIds.map((id: unknown) =>
              String(id)
            )
          });
          setRecoveryStatus('idle');
        } else {
          setRecoveryStatus('failed');
        }
      })
      .catch(() => {
        if (!cancelled) setRecoveryStatus('failed');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  const lobShellState: LobShellState | null =
    stateFromHistory ?? recoveredState;
  const {
    fetchThirdJSONs = [],
    carrierPartner = '',
    phase2LastPageName = '',
    phase2JsonFileName,
    country = '',
    province = '',
    lobApplicationIds = [applicationId],
    currentLobIndex = 0
  } = lobShellState ?? {};
  // isLastLob is derived from the queue of Phase 3 JSON filenames, not the
  // list of already-seeded application ids — additional LOBs are seeded
  // lazily after each completeApplication, so lobApplicationIds.length
  // <= fetchThirdJSONs.length until the very last hop.
  const isLastLob = currentLobIndex + 1 >= fetchThirdJSONs.length;
  const currentLobJsonFileName: string | undefined =
    fetchThirdJSONs[currentLobIndex];

  interface QueryState {
    thirdJson: Record<string, unknown> | null;
    previousAnswers: Record<string, unknown> | null;
    phase2Answers: Record<string, unknown>;
    savedAnswers: Record<string, unknown> | null;
    transactionType: string | null;
    isLoading: boolean;
  }
  const [queryState, setQueryState] = useState<QueryState>({
    thirdJson: null,
    previousAnswers: null,
    phase2Answers: {},
    savedAnswers: null,
    transactionType: null,
    isLoading: true
  });
  const {
    thirdJson,
    previousAnswers,
    phase2Answers,
    savedAnswers,
    transactionType,
    isLoading: isLoadingJson
  } = queryState;

  // Fetch Phase 3 JSON whenever the LOB target changes — covers multi-LOB
  // transitions where LobShell re-renders with a new applicationId (and
  // currentLobIndex) without unmounting. Empty-deps would leave thirdJson
  // pinned to the previous LOB's JSON and the SurveyJS model stuck in its
  // post-completion state. Skipped while currentLobJsonFileName is
  // undefined (state is still being recovered from the backend on a cold load).
  useEffect(() => {
    if (!currentLobJsonFileName) return;
    // LobShell stays mounted across LOB transitions — clear the navigation
    // flag so the new LOB's render doesn't stay stuck on Loading.
    setIsNavigatingToNextLob(false);
    setQueryState({
      thirdJson: null,
      previousAnswers: null,
      phase2Answers: {},
      savedAnswers: null,
      transactionType: null,
      isLoading: true
    });
    fetchThirdJson(apolloClient, {
      applicationId,
      thirdJsonFileName: currentLobJsonFileName
    }).then((response) => {
      const json = response?.json ?? null;
      const prevAnswers = response?.previousAnswers ?? null;
      const secondJsonAnswers =
        (response?.answersFromSecondJson as Record<string, unknown> | null) ??
        {};
      setQueryState({
        thirdJson: json,
        previousAnswers: prevAnswers,
        phase2Answers: secondJsonAnswers,
        savedAnswers:
          (response?.savedAnswers as Record<string, unknown> | null) ?? null,
        transactionType: response?.transactionType ?? null,
        isLoading: false
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, currentLobJsonFileName]);

  // Reason Flag is part of Phase 2's stored answers — derive it instead of
  // threading it through router state.
  const phase2ReasonFlag = phase2Answers['Reason Flag'] as string | undefined;

  // Free-text questions the user emptied on this LOB's pages. SurveyJS drops
  // an empty value from model.data, so without this a cleared prefilled
  // endorsement answer (previousAnswers) never reaches completeApplication
  // and the backend's lodash merge keeps the prior revision's value. Same
  // mechanism as CommercialSurveyWithApplicationId's Phase 2 handling.
  const clearedTextQuestionsRef = useRef<Set<string>>(new Set());

  const model = useMemo(() => {
    if (!thirdJson) return null;
    const m = new Model(thirdJson);
    // MET-454: the attestations question lives in the Phase 3 GL JSON, so this
    // is the page that renders its "AS REQUIRED" in bold.
    registerAttestationTextMarkdown(m);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    m.onValueChanged.add((survey: SurveyModel, options: any) => {
      const changedQuestion =
        options.question ?? survey.getQuestionByName(options.name);
      if (!isClearableTextQuestion(changedQuestion)) return;
      if (isEmptyTextAnswer(options.value)) {
        clearedTextQuestionsRef.current.add(options.name);
      } else {
        clearedTextQuestionsRef.current.delete(options.name);
      }
    });
    m.setVariable('carrierPartner', carrierPartner);
    // Inject flags as variables — they affect visibleIf but are NOT included in model.data,
    // so they won't be sent to completeApplication (backend rejects answers containing flag fields)
    m.setVariable(
      'BusinessInformation_MunichProduct',
      phase2Answers['BusinessInformation_MunichProduct']
    );
    m.setVariable(
      'BusinessInformation_Broker',
      phase2Answers['BusinessInformation_Broker']
    );
    // The Application's real transaction type, for Phase 3 JSON expressions that
    // must distinguish new business from a renewal/endorsement off a bound policy
    // — the E&O retroactive-date default is one. Set even when it is absent: an
    // unset variable makes `{transactionType} = '...'` evaluate false, silently
    // skipping the trigger instead of running it.
    m.setVariable('transactionType', transactionType ?? '');
    // Endorsement/renewal: pre-fill Munich page answers from the prior policy.
    // Same pattern as CommercialWithApplicationId for Phase 2 — strip first-page
    // fields (set fresh on the new application) and inject the rest. Phase 2
    // answers are applied afterwards so current-application values win.
    if (previousAnswers) {
      m.setVariable('isEndorsement', true);
      m.setDataCore({
        ...m.data,
        ...stripFirstPageIdentityFields(previousAnswers)
      });
    }

    // Strip flags that must not reach completeApplication (backend rejects them).
    // Munich_9219_QuoteFlag is NOT stripped here — it is injected via phase2AnswersWithoutFlags
    // so Phase 3 triggers start with the Phase 2-computed value. The Phase 3 JSON defines it
    // as visible:false, so clearInvisibleValues:"onComplete" removes it from model.data
    // automatically before completeApplication is called.
    const {
      /* eslint-disable @typescript-eslint/no-unused-vars */
      BusinessInformation_MunichProduct: _munichProduct,
      BusinessInformation_Broker: _broker,
      Beazley_5415_QuoteFlag: _beazleyFlag,
      /* eslint-enable @typescript-eslint/no-unused-vars */
      ...phase2AnswersWithoutFlags
    } = phase2Answers;
    // Back-nav to a completed LOB: restore the user's Phase 3 answers from the
    // saved completeApplication snapshot. Applied before phase2 (which reapplies
    // authoritative business/coverage below) — strip first-page identity fields,
    // same as the business page, so those stay Phase 2-authoritative.
    if (savedAnswers) {
      m.setDataCore({
        ...m.data,
        ...stripFirstPageIdentityFields(savedAnswers)
      });
    }

    // Use setDataCore + runTriggers (not direct m.data assignment) so that
    // runexpression triggers fire — in particular the workExclusionVariableText
    // trigger that computes Munich_PersonalServices_9219_WorkExclusionListHtml_WORLD_EN.
    m.setDataCore({ ...m.data, ...phase2AnswersWithoutFlags });
    m.runTriggers();

    // Retro date must not be after the requested effective date (EO only —
    // no-op when the retro question isn't in this LOB's JSON)
    setMaxDateForRetroactiveDate(m);

    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thirdJson]);

  // Endorsements are single-LOB → never seed a sibling (mirrors Phase 2 guard).
  const isEndorsement = !!model?.getVariable('isEndorsement');

  const [isComplete, setComplete] = useState(false);

  // NCP-5516: the walk is over — completed, declined or sent to a broker — so
  // the banner's claim no longer holds. Keyed on the completion state rather
  // than on each completion path: there are two of those (the survey's own
  // handler and the null-JSON fallback) and they would drift.
  useEffect(() => {
    if (isComplete) {
      clearApplicationEdit();
    }
  }, [isComplete]);
  const [isRedirectToQuote, setRedirectToQuote] = useState(false);
  // Edit Identity: the walk finished but the quote changed on the quote page
  // while it was open, so nothing was submitted. Holds the group to return to.
  const [editConflictGroupId, setEditConflictGroupId] = useState<string | null>(
    null
  );
  // A completion the backend refused while this tab's walk is still open.
  const [walkErrorMsg, setWalkErrorMsg] = useState<string | null>(null);
  // The null-JSON fallback completes during render; this keeps it to one call
  // per attempt. Its error modal offers "Try again", which clears it.
  const fallbackCompletionStartedRef = useRef(false);
  const [isNavigatingToNextLob, setIsNavigatingToNextLob] = useState(false);
  // Captured from the completeApplication response so the final redirect
  // goes to `/quote/<applicationGroupId>` per master's PR #1153 route rename.
  const [
    completedApplicationGroupId,
    setCompletedApplicationGroupId
  ] = useState<string | null>(null);

  const [
    completeApplicationMutation,
    { loading: isCompleting }
  ] = useCompleteApplicationMutation();
  // Cold-load recovery render gate: keep rendering Loading while the backend
  // fills in the LobShell state, redirect home if it can't (no group, missing
  // lobType on a child, etc.). Single-flow (state-from-history) renders skip
  // this branch entirely — `recoveryStatus` stays 'idle' there.
  // Edit Identity: a completion was refused. When this tab's walk is gone the
  // customer is led out, since nothing typed here can be saved; otherwise the
  // error stands.
  const showWriteFailure = async (message: string) => {
    try {
      const walk = await readTabWalk(apolloClient, applicationId);
      if (walk.status !== 'current') {
        setWalkExit(walk);
        return;
      }
    } catch {
      // The read failed too: show the original error.
    }
    setWalkErrorMsg(message);
  };

  // On to the walk's next LOB. The Phase 2 boundary prepared every queued
  // LOB's Application, so the queue already names it.
  const goToNextLob = async () => {
    const nextIndex = currentLobIndex + 1;
    const nextApplicationId = lobApplicationIds[nextIndex];
    if (!nextApplicationId) {
      throw new Error('Phase 3 queue has no Application for the next LOB');
    }
    setIsNavigatingToNextLob(true);
    const { data: walkData } = await apolloClient.query<
      GetApplicationWalkQuery,
      GetApplicationWalkQueryVariables
    >({
      query: GetApplicationWalkDocument,
      variables: { id: nextApplicationId },
      fetchPolicy: 'network-only'
    });
    // A LOB the walk added is in its queue but not yet a group member.
    const nextLobType =
      walkData?.getApplicationGroup?.openEdit?.queue.find(
        (entry) =>
          entry.applicationId?.toString() === nextApplicationId.toString()
      )?.lobType ?? '';
    history.push(`/${nextLobType}/${nextApplicationId}`, {
      ...lobShellState,
      currentLobIndex: nextIndex
    });
  };

  if (editConflictGroupId) {
    return (
      <Modal
        onAction={() => history.push(`/quote/${editConflictGroupId}`)}
        actionName="Review quote"
      >
        <div className="text-primary">
          <p>{EDIT_CONFLICT_MESSAGE}</p>
        </div>
      </Modal>
    );
  }
  if (walkExit) {
    return (
      <WalkExitModal status={walkExit.status} exitPath={walkExit.exitPath} />
    );
  }
  if (walkErrorMsg) {
    const isFallback = !thirdJson || !model;
    return (
      <Modal
        onAction={() => {
          // Dismissing the fallback's error retries it: there is no survey
          // page behind it to return to.
          fallbackCompletionStartedRef.current = false;
          setWalkErrorMsg(null);
        }}
        actionName={isFallback ? 'Try again' : undefined}
      >
        <div className="text-primary">
          <p>{walkErrorMsg}</p>
        </div>
      </Modal>
    );
  }

  if (recoveryStatus === 'loading') return <Loading />;
  if (recoveryStatus === 'failed' || !lobShellState) {
    return <Redirect to="/" />;
  }

  if (isLoadingJson || isCompleting || isNavigatingToNextLob)
    return <Loading />;

  if (isComplete) {
    if (isRedirectToQuote) {
      if (!completedApplicationGroupId) {
        throw new Error(
          'applicationGroupId missing from completeApplication response'
        );
      }
      // NCP-5129: tell the quote page it was reached straight from a completed
      // survey, so it sends the consolidated group email even when the round
      // produces no new quote. A LOB added from the quote page and then declined
      // has no quote of its own and leaves its siblings as cache hits, so
      // isNewQuote alone would report nothing. Navigating here unmounts the
      // quote page, so an in-page flag cannot survive the trip — the marker has
      // to ride the redirect.
      return (
        <Redirect
          to={{
            pathname: `/quote/${completedApplicationGroupId}`,
            state: { arrivedFromSurvey: true }
          }}
        />
      );
    }
    return (
      <Redirect to={{ pathname: '/complete', state: { country, province } }} />
    );
  }

  // GA quote funnel: the application could not be bound online.
  const trackReferral = (kind: ApplicationResultKind) =>
    trackEvent('quote_referred', {
      referral_reason:
        kind === ApplicationResultKind.DeclineBusiness
          ? 'decline_business'
          : 'send_to_broker',
      product: toProduct([lobType]) || undefined
    });

  // Fallback: getThirdJson returned null (edge case — e.g. sendToBrokerFlag toggled between
  // Phase 2 submit and Phase 3 load). Call completeApplication directly.
  if (!thirdJson || !model) {
    if (fallbackCompletionStartedRef.current) return <Loading />;
    fallbackCompletionStartedRef.current = true;
    const {
      /* eslint-disable @typescript-eslint/no-unused-vars */
      BusinessInformation_MunichProduct: _munichProduct,
      Munich_9219_QuoteFlag: _munichFlag,
      BusinessInformation_Broker: _broker,
      Beazley_5415_QuoteFlag: _beazleyFlag,
      /* eslint-enable @typescript-eslint/no-unused-vars */
      ...answersWithoutFlags
    } = phase2Answers;

    // Completing from Phase 2 answers — keep single-LOB (drop sibling coverage).
    stripSiblingCoverage(answersWithoutFlags, lobType);
    // Endorsement: drop inherited Amtrust/WC so it doesn't leak into GL answers.
    if (previousAnswers) stripAmtrustCoverage(answersWithoutFlags);
    // phase2 dates may be ISO datetime echoes of stored Dates — format to yyyy-MM-dd
    formatDateAnswers(answersWithoutFlags);

    completeApplicationMutation({
      variables: {
        answersInfo: {
          applicationId,
          answers: answersWithoutFlags,
          isBrokerFlow: !!(
            phase2Answers['BusinessInformation_Broker'] ||
            !phase2Answers['Munich_9219_QuoteFlag']
          ),
          reasonFlag: phase2ReasonFlag
        },
        pageName: phase2LastPageName,
        // Phase 3 JSON failed to load — we're completing from the Phase 2
        // answer state, so the row's source JSON is the biz_coverage filename.
        jsonName: phase2JsonFileName,
        editId: getApplicationEditId()
      }
    })
      .then(async ({ data: result }) => {
        if (!result) throw new Error('completeApplicationData should be ready');
        const {
          errors,
          kind,
          applicationGroupId: resultApplicationGroupId,
          editConflict
        } = result.completeApplication;
        if (errors.length > 0) {
          // Inside a walk the customer needs to read it; elsewhere this path
          // keeps the user on Loading, as before.
          if (!getApplicationEditId()) throw new Error(errors.join(' - '));
          await showWriteFailure(errors.join(' - '));
          return;
        }
        if (editConflict) {
          clearApplicationEdit();
          setEditConflictGroupId(resultApplicationGroupId);
          return;
        }
        if (
          kind === ApplicationResultKind.OfferQuote &&
          !isLastLob &&
          !isEndorsement
        ) {
          await goToNextLob();
          return;
        }
        setCompletedApplicationGroupId(resultApplicationGroupId);
        if (kind === ApplicationResultKind.OfferQuote) {
          setRedirectToQuote(true);
        } else {
          trackReferral(kind);
        }
        setComplete(true);
      })
      .catch(() => {
        // A missing completeApplication payload or a backend-reported error in
        // the fallback path is non-recoverable here — leave the user on Loading
        // (no navigation/redirect) rather than letting the rejection go
        // unhandled or crashing the render.
      });

    return <Loading />;
  }

  // Munich path: show the LOB survey page
  const doComplete = async () => {
    // Capture fields before completeLastPage() — SurveyJS's default
    // clearInvisibleValues:"onComplete" removes invisible questions from model.data.
    const preCompleteMunichFlag = model.data['Munich_9219_QuoteFlag'];
    const preCompleteReasonFlag =
      (model.data['Reason Flag'] as string | undefined) || phase2ReasonFlag;
    // WorkExclusionListHtml is visible:false in Phase 3 JSON → cleared by
    // clearInvisibleValues:"onComplete". Capture before completeLastPage().
    const preCompleteWorkExclusionListHtml =
      model.data['Munich_PersonalServices_9219_WorkExclusionListHtml_WORLD_EN'];
    // WorkExclusionText is an html-type question — html questions don't store values
    // in model.data, so read the html property directly from the question object.
    const workExclusionTextQuestion = model.getQuestionByName(
      'Munich_PersonalServices_9219_WorkExclusionText_WORLD_EN'
    );
    const preCompleteWorkExclusionText = workExclusionTextQuestion
      ? (workExclusionTextQuestion as { html?: string }).html
      : undefined;
    const lastPageName = model.currentPage.name;
    const surveyValidateResult = model.completeLastPage();
    if (!surveyValidateResult) {
      model.currentPage.focusFirstErrorQuestion();
      return;
    }

    const { data: modelData } = model;
    formatDateAnswers(modelData);

    // Strip fetchThirdJSONs — it's a trigger variable (Phase 3 filename), not a user answer
    delete modelData['fetchThirdJSONs'];

    // Keep this LOB's saved answers single-LOB — drop the sibling's coverage toggle.
    stripSiblingCoverage(modelData, lobType);
    // Endorsement inherits the bound policy's answers, including Amtrust/WC —
    // Phase 3 GL has no WC questions, so drop it to keep the GL answers clean.
    if (isEndorsement) stripAmtrustCoverage(modelData);
    // Re-send emptied free-text answers as '' so the removal is recorded.
    applyClearedTextAnswers(modelData, clearedTextQuestionsRef.current, model);

    const combinedAnswers = {
      ...modelData,
      BusinessInformation_100_BusinessAddress_WORLD_EN:
        phase2Answers.BusinessInformation_100_BusinessAddress_WORLD_EN,
      BusinessInformation_100_CustomerInfo_WORLD_EN:
        phase2Answers.BusinessInformation_100_CustomerInfo_WORLD_EN,
      BusinessInformation_100_EffectiveDate_WORLD_EN:
        phase2Answers.BusinessInformation_100_EffectiveDate_WORLD_EN,
      BusinessInformation_100_CompanyName_WORLD_EN:
        phase2Answers.BusinessInformation_100_CompanyName_WORLD_EN,
      Munich_PersonalServices_9219_WorkExclusionListHtml_WORLD_EN: preCompleteWorkExclusionListHtml,
      Munich_PersonalServices_9219_WorkExclusionText_WORLD_EN: preCompleteWorkExclusionText
    };
    // re-attached phase2 dates may be ISO datetime echoes of stored Dates —
    // format after composing so the backend gets yyyy-MM-dd
    formatDateAnswers(combinedAnswers);

    const answersInfo = {
      applicationId,
      answers: combinedAnswers,
      isBrokerFlow: !!(
        phase2Answers['BusinessInformation_Broker'] || !preCompleteMunichFlag
      ),
      reasonFlag: preCompleteReasonFlag
    };

    const { data: result } = await completeApplicationMutation({
      variables: {
        answersInfo,
        pageName: model.currentPage.name,
        // Current page belongs to the Phase 3 LOB JSON — that's the JSON the
        // user just completed and the row should be stamped with.
        jsonName: currentLobJsonFileName,
        editId: getApplicationEditId()
      }
    });

    if (!result) throw new Error('completeApplicationData should be ready');

    const {
      errors,
      kind,
      applicationGroupId: resultApplicationGroupId,
      editConflict
    } = result.completeApplication;
    if (errors.length > 0) {
      // Inside a walk the customer needs to read it (most often: the walk was
      // replaced in another tab). Elsewhere it stays a failure.
      if (!getApplicationEditId()) throw new Error(errors.join(' - '));
      await showWriteFailure(errors.join(' - '));
      return;
    }
    if (editConflict) {
      clearApplicationEdit();
      setEditConflictGroupId(resultApplicationGroupId);
      return;
    }
    setCompletedApplicationGroupId(resultApplicationGroupId);
    trackQuoteStep(lastPageName, toProduct([lobType]));

    switch (kind) {
      case ApplicationResultKind.OfferQuote:
        if (!isLastLob && !isEndorsement) {
          await goToNextLob();
          return;
        }
        setRedirectToQuote(true);
        break;
      case ApplicationResultKind.SendToBroker:
      case ApplicationResultKind.DeclineBusiness:
        trackReferral(kind);
        setRedirectToQuote(false);
        break;
      default:
        throw new Error('Unreachable - unexpected Application result state');
    }
    setComplete(true);
  };

  return (
    <div className="h-full Commercial" data-testid="LobShell">
      {/* NCP-5516: the walk is not submitted until its last LOB is completed,
          so the bar rides every Phase 3 page too, not just Phase 2. */}
      <Banner
        isVisible={isApplicationEditInProgress()}
        message={RESUBMIT_TO_SAVE_MESSAGE}
      />
      <div className="survey-container">
        <Survey model={model} css={SurveyCSS} />
        <Navigation
          goBack={() => {
            // Back on the first LOB returns to Phase 2, which remounts on its
            // first page by default — resume it on the page the user left
            // (the coverage page) instead.
            if (currentLobIndex === 0 && phase2LastPageName) {
              setPhase2ResumePageName(phase2LastPageName);
            }
            history.goBack();
          }}
          goNext={undefined}
          doComplete={doComplete}
        />
      </div>
    </div>
  );
};

export default LobShell;
